import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@telegram-apps/telegram-ui";
import { retrieveLaunchParams, shareURL } from "@telegram-apps/sdk";
import { TELEGRAM_APP_URL } from "../../configs/config";
import { useI18n } from "../../utils/i18n/I18nProvider";
import "./InvitePromoModal.css";

interface InvitePromoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function InvitePromoModal({
  isOpen,
  onClose,
}: InvitePromoModalProps): JSX.Element | null {
  const { t } = useI18n();
  const [inviteLink, setInviteLink] = useState<string>("");
  const [isCopied, setIsCopied] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;
    try {
      const params = retrieveLaunchParams();
      const userId = params.tgWebAppData?.user?.id;
      if (userId) {
        setInviteLink(`${TELEGRAM_APP_URL}?startapp=${userId}`);
      }
    } catch (error) {
      console.error("InvitePromoModal: failed to build invite link", error);
    }
  }, [isOpen]);

  const copyIcon = useMemo(
    () => (
      <span className="invite-promo-button-icon">
        {isCopied ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
              fill="currentColor"
            />
          </svg>
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16 8V5.2C16 4.0799 16 3.51984 15.782 3.09202C15.5903 2.71569 15.2843 2.40973 14.908 2.21799C14.4802 2 13.9201 2 12.8 2H5.2C4.0799 2 3.51984 2 3.09202 2.21799C2.71569 2.40973 2.40973 2.71569 2.21799 3.09202C2 3.51984 2 4.0799 2 5.2V12.8C2 13.9201 2 14.4802 2.21799 14.908C2.40973 15.2843 2.71569 15.5903 3.09202 15.782C3.51984 16 4.0799 16 5.2 16H8M11.2 22H18.8C19.9201 22 20.4802 22 20.908 21.782C21.2843 21.5903 21.2843 21.2843 21.782 20.908C22 20.4802 22 19.9201 22 18.8V11.2C22 10.0799 22 9.51984 21.782 9.09202C21.5903 8.71569 21.2843 8.40973 20.908 8.21799C20.4802 8 19.9201 8 18.8 8H11.2C10.0799 8 9.51984 8 9.09202 8.21799C8.71569 8.40973 8.40973 8.71569 8.21799 9.09202C8 9.51984 8 10.0799 8 11.2V18.8C8 19.9201 8 20.4802 8.21799 20.908C8.40973 21.2843 8.71569 21.5903 9.09202 21.782C9.51984 22 10.0799 22 11.2 22Z"
              stroke="#171F2A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
    ),
    [isCopied]
  );

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("InvitePromoModal: failed to copy", error);
    }
  }

  function handleShare() {
    try {
      shareURL(inviteLink);
    } catch (error) {
      console.error("InvitePromoModal: failed to share", error);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="invite-promo-modal">
      <div className="invite-promo-sheet">
        <button
          className="invite-promo-close-button"
          onClick={onClose}
          aria-label={t("common.close")}
        >
          ×
        </button>
        <img
          className="invite-promo-illustration"
          src="/assets/img/ui/coin.webp"
          alt=""
        />
        <div className="invite-promo-content">
          <div className="invite-promo-text-block">
            <span className="invite-promo-text">
              {t("toolkit.invitePromo.line1")}
            </span>
            {/* <img
              src="/assets/img/ui/coin.webp"
              alt="Coin"
              className="invite-promo-coin"
            />
            */}
          </div>
          <div className="invite-promo-text-block">
            <span className="invite-promo-text">
              {t("toolkit.invitePromo.line2")}
            </span>
            {/* <img
              src="/assets/img/ui/coin.webp"
              alt="Coin"
              className="invite-promo-coin"
            />
            */}
          </div>
        </div>
        <div className="invite-promo-buttons">
          <Button
            className="invite-promo-copy"
            mode="bezeled"
            size="l"
            stretched
            after={copyIcon}
            onClick={handleCopy}
          >
            {t("toolkit.invitePromo.copyLink")}
          </Button>
          <Button
            className="invite-promo-share"
            mode="filled"
            size="l"
            stretched
            onClick={handleShare}
          >
            {t("common.share")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default InvitePromoModal;
